import { Link, useParams } from 'react-router-dom'
import { AppShell } from '../components/AppShell'
import { useBets, useCancelBet, type Bet } from '../lib/api'

function fmtEur(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
}
function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-IE', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const STATUS_CHIP: Record<Bet['status'], { label: string; cls: string }> = {
  pending:    { label: 'Pending',    cls: 'chip--pending' },
  won:        { label: 'Won',        cls: 'chip--won' },
  lost:       { label: 'Lost',       cls: 'chip--lost' },
  void:       { label: 'Void',       cls: 'chip--void' },
  cancelled:  { label: 'Cancelled',  cls: 'chip--void' },
  cashed_out: { label: 'Cashed out', cls: 'chip--won' },
}

export function MatchDetailPage() {
  const { betId } = useParams<{ betId: string }>()
  const bets = useBets()
  const cancel = useCancelBet()
  const bet = bets.data?.find((b) => b.id === betId)

  if (bets.isLoading) {
    return (
      <AppShell page="bets" breadcrumb={<span className="topbar__breadcrumb-current">Bet detail</span>}>
        <div style={{ color: 'var(--text-tertiary)', padding: 'var(--space-5)' }}>Loading…</div>
      </AppShell>
    )
  }

  if (!bet) {
    return (
      <AppShell page="bets" breadcrumb={<span className="topbar__breadcrumb-current">Bet not found</span>}>
        <header className="page-header">
          <div className="page-header__main">
            <p className="page-header__eyebrow">
              <Link to="/bets" style={{ textDecoration: 'none' }}>← My Bets</Link>
            </p>
            <h1 className="page-header__title">Bet not found</h1>
            <p className="page-header__subtitle">No bet with that ID in your history.</p>
          </div>
        </header>
        <div className="empty-state">
          <div className="empty-state__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="12" cy="12" r="9" />
              <path d="M9 9 l6 6 M15 9 l-6 6" />
            </svg>
          </div>
          <h3 className="empty-state__title">Nothing here</h3>
          <p className="empty-state__body">The bet may have been removed, or the link is wrong.</p>
        </div>
      </AppShell>
    )
  }

  const chip = STATUS_CHIP[bet.status]
  const pnl = Number(bet.pnl || 0)
  const stake = Number(bet.stake || 0)
  const odds = Number(bet.oddsDecimal || 0)
  const potential = stake * odds

  return (
    <AppShell page="bets" breadcrumb={<span className="topbar__breadcrumb-current">Bet {bet.id.slice(0, 8)}…</span>}>
      <header className="page-header">
        <div className="page-header__main">
          <p className="page-header__eyebrow">
            <Link to="/bets" style={{ textDecoration: 'none' }}>← My Bets</Link>
          </p>
          <h1 className="page-header__title">{bet.eventLabel ?? `Event ${bet.eventId.slice(0, 8)}…`}</h1>
          <p className="page-header__subtitle" style={{ fontFamily: 'var(--font-mono)' }}>
            #{bet.id.slice(0, 12)}
          </p>
        </div>
        <div className="page-header__actions">
          <span className={`chip ${chip.cls}`}>
            <span className="chip__dot" aria-hidden />{chip.label}
          </span>
          {bet.status === 'pending' && (
            <button type="button" className="btn btn--destructive btn--sm" disabled={cancel.isPending}
              onClick={() => { if (window.confirm('Cancel this bet?')) cancel.mutate(bet.id) }}>
              Cancel bet
            </button>
          )}
        </div>
      </header>

      <section className="match-hero" style={{ marginBottom: 'var(--space-5)' }}>
        <div className="match-hero__meta">
          {bet.sport && <span className="match-hero__league">{bet.sport}</span>}
          <span className="match-hero__time">{fmtDateTime(bet.placedAt)}</span>
        </div>
        <div className="match-hero__main">
          <h2 className="match-hero__title">{bet.eventLabel ?? '—'}</h2>
        </div>
      </section>

      <section className="kpi-grid" aria-label="Bet stats"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-3)', marginBottom: 'var(--space-5)' }}>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Stake</span></header>
          <div className="kpi-card__value">{fmtEur(stake)}</div>
        </article>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Odds</span></header>
          <div className="kpi-card__value">{odds.toFixed(2)}</div>
        </article>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Potential win</span></header>
          <div className="kpi-card__value">{fmtEur(potential)}</div>
        </article>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">P&amp;L</span></header>
          <div className="kpi-card__value" style={{ color: pnl >= 0 ? 'var(--color-success-text)' : 'var(--color-danger-text)' }}>
            {pnl >= 0 ? '+' : ''}{fmtEur(pnl)}
          </div>
        </article>
      </section>

      <section className="settings-card">
        <header className="settings-card__header"><h2 className="settings-card__title">Selection</h2></header>
        <div className="settings-card__body">
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 'var(--space-4)', rowGap: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
            <dt style={{ color: 'var(--text-tertiary)' }}>Pick</dt>
            <dd style={{ fontWeight: 600 }}>{bet.selectionName}</dd>
            <dt style={{ color: 'var(--text-tertiary)' }}>Market ID</dt>
            <dd style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{bet.marketId}</dd>
            <dt style={{ color: 'var(--text-tertiary)' }}>Event ID</dt>
            <dd style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{bet.eventId}</dd>
            <dt style={{ color: 'var(--text-tertiary)' }}>Placed</dt>
            <dd>{fmtDateTime(bet.placedAt)}</dd>
            {bet.settledAt && (<>
              <dt style={{ color: 'var(--text-tertiary)' }}>Settled</dt>
              <dd>{fmtDateTime(bet.settledAt)}</dd>
            </>)}
          </dl>
        </div>
      </section>
    </AppShell>
  )
}
